import { useEffect, useMemo, useState } from "react";

// Interfaces
import { CitContentDetailsProps, ICitContentDetails } from "./interfaces";
import { ISelectableExportHistory, ISelectableRecommendedAction, IWriteOffPreviewFilter, TActivityType, TFormatType } from "../interfaces";

// Helpers
import { or } from "@/Common/helpers/sonar";

// MUI
import { Stack } from "@mui/material";

// Layouts
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// Components
import CitActivitiesTableModel from "../CitActivitiesTableModel";
import CitProcessOptions from "../CitProcessOptions";

// Constants
import { EXPORT_FORMAT_TYPES } from "../CitProcessOptions/helper";
import { ICitActivity } from "@/Common/types/ContractsInTransit/model";
import uniqBy from "lodash-es/uniqBy";
import compact from "lodash-es/compact";
const FILTER_OPTIONS: ISelectableRecommendedAction[] = [{ label: "Recommended for Clearing" }, { label: "Consider Reviewing" }];

const getTableTitle = (activityType: TActivityType) => {
	if (activityType === "CIT") {
		return "Contracts in Transit";
	}
	return activityType;
};

export default function CitContentDetails(props: Readonly<CitContentDetailsProps>) {
	const {
		identifier,
		format,
		hierarchyKey,
		activityType,
		isShowing,
		disabled = false,
		excludedExport = false,
		activitiesIds = [],
		activities = [],
		onUpdateAction = () => {},
		selectableExportHistory,
	} = props ?? {};

	// States
	const [model, setModel] = useState<ICitContentDetails>({
		identifier,
		activityType,
		format,
		activitiesIds,
	});
	const [recommendedActionFilter, setRecommendedActionFilter] = useState<ISelectableRecommendedAction | null>(null);
	const [exportEntryFilter, setExportEntryFilter] = useState<ISelectableExportHistory[]>([]);

	const previewFilters = useMemo<IWriteOffPreviewFilter>(
		() => ({
			recommendedAction: recommendedActionFilter?.label,
			exportEntryIds: exportEntryFilter?.map((entry) => entry.id) ?? [],
		}),
		[recommendedActionFilter, exportEntryFilter]
	);

	const handleExportFilterChange = (newValue: ISelectableExportHistory[]) => {
		if (newValue.length) {
			setExportEntryFilter(uniqBy(compact([...newValue]), "id"));
			setRecommendedActionFilter(null);
		} else {
			setExportEntryFilter([]);
		}
	};
	const handleRecommendedActionFilterChange = (newValue: ISelectableRecommendedAction | null) => {
		setRecommendedActionFilter(newValue);
		if (newValue) {
			setExportEntryFilter([]);
		}
	};

	const handleUpdateFormat = (value: string) => {
		const updatedFormat = value as TFormatType;
		setModel((prev) => ({ ...prev, format: updatedFormat }));
		onUpdateAction?.({ ...model, format: updatedFormat });
	};

	const handleUpdateActivities = (selectedActivities: string[]) => {
		setModel((prev) => ({ ...prev, activitiesIds: selectedActivities }));
		onUpdateAction?.({ ...model, activitiesIds: selectedActivities });
	};

	useEffect(() => {
		setModel({
			identifier,
			activityType,
			format,
			activitiesIds,
		});
	}, [identifier, activityType, format, activitiesIds]);

	const disabledActions = or(Boolean(disabled), !activities?.length);
	const completeDisabledActions = or(Boolean(disabledActions), Boolean(excludedExport));

	let finalActivities = activities;

	if (recommendedActionFilter) {
		finalActivities = activities.filter((activity) => {
			return activity.recommendedAction === recommendedActionFilter.label;
		});
	}
	if (exportEntryFilter?.length > 0) {
		finalActivities = exportEntryFilter.flatMap(
			(entry) => selectableExportHistory.groupedActivities?.[entry.id] ?? []
		) as ICitActivity[];
	}

	return (
		<Stack>
			<Stack direction={"row"} alignItems="center">
				<CitProcessOptions
					defaultOption={model.format}
					options={EXPORT_FORMAT_TYPES}
					disabled={completeDisabledActions}
					onProcessChange={handleUpdateFormat}
				/>
			</Stack>
			<Stack position={"relative"} sx={{ zIndex: 101 }}>
				<Stack position={"absolute"} direction={"row"} gap={3} alignItems="center" marginTop={"14px"} minWidth={"216px"}>
					<Stack minWidth={"264px"}>
						<SmartSelector
							showBy={"label"}
							selectBy={"label"}
							options={FILTER_OPTIONS}
							selected={recommendedActionFilter}
							inputProps={{
								placeholder: "Group Inclusions",
							}}
							listBoxProps={{
								sx: { maxHeight: "220px" },
							}}
							popperProps={{ className: "!z-[1350]" } as object}
							clearable
							disabled={disabledActions}
							onChangeAction={(value) => handleRecommendedActionFilterChange(value as ISelectableRecommendedAction)}
						/>
					</Stack>
					<Stack minWidth={"264px"}>
						<SmartSelector
							multiple
							tagsVariant={"text"}
							showBy={"label"}
							selectBy={"id"}
							options={selectableExportHistory.possibleExports ?? []}
							selected={exportEntryFilter}
							inputProps={{
								placeholder: "Export Filter",
								classes: "!max-w-[700px]",
							}}
							listBoxProps={{
								sx: { maxHeight: "220px" },
							}}
							popperProps={{ className: "!z-[1350]" } as object}
							clearable
							disabled={disabledActions}
							onChangeAction={(value) => handleExportFilterChange(value as ISelectableExportHistory[])}
						/>
					</Stack>
				</Stack>
			</Stack>
			<Stack>
				<CitActivitiesTableModel
					title={getTableTitle(activityType)}
					activities={finalActivities}
					activitiesIds={activitiesIds}
					hierarchyKey={hierarchyKey}
					filter={previewFilters}
					disabled={completeDisabledActions}
					isShowing={isShowing}
					isLoading={false}
					isFetching={false}
					onUpdateAction={handleUpdateActivities}
				/>
			</Stack>
		</Stack>
	);
}
